import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";

interface IRadioButtonControlProps<TKey, TItem> {
    id?: string;
    radioInputGroupId: string;
    items: Immutable.List<KeyValuePair<TKey, TItem>>;
    onePerLine?: boolean;
    title?: string;

    changeOperation?: number;
    onChange?: (item: KeyValuePair<TKey, TItem>) => void;
    selectedItem: TItem;

    /**
     * Whether the radio button list is disabled, so the user can't change the value. Default false.
     */
    disabled?: boolean;

    /**
     * Set whether an option should be disabled, so it can't be selected. If a disabled item is already selected, the
     * user can change it to a non-disabled option but won't be able to switch it back.
     *
     * This has no effect if {@link disabled} is `true`.
     *
     * @param item The item to check.
     */
    disableItem?: (item: KeyValuePair<TKey, TItem>) => boolean;

    onBlur?: () => void;

    mainDivCSS?: string;
    radioItemParentStyle?: React.CSSProperties;
    radioItemStyle?: React.CSSProperties;
    radioInputStyle?: React.CSSProperties;
}

class RadioButton<TKey, TItem extends boolean | number | string> extends React.Component<IRadioButtonControlProps<TKey, TItem>, never> {
    private controlId: string;

    constructor(props: IRadioButtonControlProps<TKey, TItem>) {
        super(props);
        if (this.props.id == null) {
            this.controlId = GuidHelper.NewGuid(); // generate self id
        }
        else {
            this.controlId = this.props.id;
        }
    }

    public shouldComponentUpdate(nextProps: IRadioButtonControlProps<TKey, TItem>): boolean {
        var val = nextProps.selectedItem !== this.props.selectedItem;
        var list = nextProps.items !== this.props.items;
        return val || list;
    }

    public render(): JSX.Element {
        var title = null;

        var css = "settings-indent";
        if (this.props.mainDivCSS != null) { css = this.props.mainDivCSS; }

        let radioItemStyle: React.CSSProperties = { marginTop: "0px", marginBottom: "0px" };
        if (this.props.radioItemStyle != null) {
            radioItemStyle = this.props.radioItemStyle;
            radioItemStyle.marginTop = this.props.radioItemStyle.marginTop ? this.props.radioItemStyle.marginTop : radioItemStyle.marginTop; // Apply Default Value
            radioItemStyle.marginBottom = this.props.radioItemStyle.marginBottom ? this.props.radioItemStyle.marginBottom : radioItemStyle.marginBottom; // Apply Default Value
        }

        let radioItemParentStyle: React.CSSProperties = { paddingRight: "10px" };
        if (this.props.radioItemParentStyle != null) {
            radioItemParentStyle = this.props.radioItemParentStyle;
            radioItemParentStyle.marginTop = this.props.radioItemParentStyle.marginTop ? this.props.radioItemParentStyle.marginTop : radioItemParentStyle.paddingRight; // Apply Default Value
        }

        let radioInputStyle: React.CSSProperties = {};
        if (this.props.radioInputStyle != null) {
            radioInputStyle = this.props.radioInputStyle;
        }

        if (this.props.title != null)
            title = <label className="ratio-title" style={{ marginBottom: "0px" }}>{this.props.title}</label>;

        if (this.props.onePerLine === true)
            return this.blockComponent(title, css, radioItemStyle);
        else
            return this.inlineComponent(title, css, radioItemStyle, radioItemParentStyle, radioInputStyle);
    }

    private inlineComponent(title: string, css: string, radioItemStyle: React.CSSProperties, radioItemParentStyle: React.CSSProperties, radioInputStyle: React.CSSProperties): JSX.Element {
        return (
            <div>
                {title}
                <div className={"tb " + css}>
                    {this.props.items.toArray().map((item, index: number) => {
                        const disabled = this.props.disabled || this.props.disableItem?.(item) === true;

                        return (
                            <div className="tbc" style={radioItemParentStyle} key={index + "_list_item_" + this.controlId}>
                                <div className="radio" style={radioItemStyle}>
                                    <label htmlFor={index + "_list_item_" + this.controlId} style={disabled ? { cursor: "not-allowed" } : {}}>
                                        <input
                                            disabled={disabled}
                                            style={radioInputStyle}
                                            onChange={() => this.onSelectionChange(item)}
                                            id={index + "_list_item_" + this.controlId}
                                            type="radio"
                                            name={this.props.radioInputGroupId}
                                            value={item.Value?.toString()}
                                            checked={this.props.selectedItem === item.Value}
                                            onBlur={() => this.props.onBlur?.()}
                                        />
                                        {item.Key}
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    private blockComponent(title: string, css: string, radioItemStyle: React.CSSProperties): JSX.Element {
        return (
            <div>
                {title}
                <ul className={css}>
                    {this.props.items.toArray().map((item, index) => (
                        <li key={index + "_list_item_" + this.controlId} data-cy={`${this.props.radioInputGroupId}_${item.Value}`} className={this.props.onePerLine == true ? "fullWidth" : ""}>
                            <div className="radio" style={radioItemStyle}>
                                <label htmlFor={index + "_list_item_" + this.controlId}>
                                    <input
                                        onChange={() => this.onSelectionChange(item)}
                                        id={index + "_list_item_" + this.controlId}
                                        type="radio"
                                        name={this.props.radioInputGroupId}
                                        value={item.Value?.toString()}
                                        checked={this.props.selectedItem === item.Value}
                                        disabled={this.props.disabled}
                                        onBlur={() => this.props.onBlur?.()}
                                    />
                                    {item.Key}
                                </label>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    private onSelectionChange(item: KeyValuePair<TKey, TItem>): void {
        this.props.onChange?.(item);

        if (this.props.changeOperation)
            sessionStore.dispatch(this.props.changeOperation, { value: item });
    }
}

export { RadioButton };
